import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './ResponsiveBreadcrumb.css';
import BreadcrumbItem from '../atoms/BreadcrumbItem';

// Lightweight wrapper so breadcrumb children can be declared as <ResponsiveBreadcrumb.Item>.
const ResponsiveBreadcrumbItem = ({ children: itemChildren }) => itemChildren ?? null;

// Flattens nested children/fragments into a single breadcrumb item array.
const flattenBreadcrumbChildren = (childrenNodes, out = []) => {
   React.Children.toArray(childrenNodes).forEach((child, idx) => {
      if (!child) return;

      if (!React.isValidElement(child)) {
         out.push({ key: `raw-${idx}`, node: child });
         return;
      }

      if (child.type === React.Fragment) {
         flattenBreadcrumbChildren(child.props?.children, out);
         return;
      }

      if (child.type === ResponsiveBreadcrumbItem) {
         out.push({
            key: child.key ?? `item-${out.length}`,
            ...child.props,
            node: child.props?.children,
         });
         return;
      }

      out.push({
         key: child.key ?? `node-${out.length}`,
         node: child,
      });
   });

   return out;
};

// Normalizes mixed item inputs into a consistent { key, node, ...rest } shape.
const normalizeItem = (item, idx) => {
   if (item == null) {
      return null;
   }

   if (React.isValidElement(item) || typeof item !== 'object') {
      return {
         key: `item-${idx}`,
         node: item,
      };
   }

   return {
      ...item,
      key: item.key ?? `item-${idx}`,
      node: item.node ?? item.title ?? item.label ?? item.children,
   };
};

// Returns true for primitive renderable labels that can be wrapped as text links/buttons.
const isSimpleType = (value) => typeof value === 'string' || typeof value === 'number';

const getNodeText = (node) => {
   if (isSimpleType(node)) {
      return String(node);
   }

   if (!React.isValidElement(node)) {
      return '';
   }

   const children = node.props?.children;

   if (isSimpleType(children)) {
      return String(children);
   }

   if (Array.isArray(children)) {
      return children
         .map(getNodeText)
         .join('')
         .trim();
   }

   return getNodeText(children);
};

const isHomeBreadcrumbItem = (item) => {
   const key = String(item?.key ?? '').toLowerCase();
   const label = getNodeText(item?.node).trim().toLowerCase();
   const destination = String(item?.href ?? item?.node?.props?.to ?? item?.node?.props?.href ?? '').toLowerCase();

   return key === 'home' || (label === 'home' && ['/home', 'home'].includes(destination));
};

// Responsive breadcrumb that collapses middle items into an overflow menu when width is limited.
const ResponsiveBreadcrumb = ({
   items = [],
   children,
   separator = '>',
   keepStart = 1,
   keepEnd = 1,
   hideFrom = 'start',
   style,
}) => {
   const containerRef = useRef(null);
   const menuRef = useRef(null);
   const [hiddenCount, setHiddenCount] = useState(0);
   const [layoutTick, setLayoutTick] = useState(0);
   const [isMenuOpen, setIsMenuOpen] = useState(false);

   const normalizedItems = useMemo(() => {
      // Support either `items` prop objects or JSX children with a single normalized shape.
      if (Array.isArray(items) && items.length > 0) {
         return items.map(normalizeItem).filter(Boolean).filter((item) => !isHomeBreadcrumbItem(item));
      }

      return flattenBreadcrumbChildren(children).map(normalizeItem).filter(Boolean).filter((item) => !isHomeBreadcrumbItem(item));
   }, [items, children]);

   // Use a stable signature so inline `items={[...]}` arrays don't trigger reset loops.
   const normalizedItemsSignature = useMemo(() => {
      // Signature avoids effect loops when callers pass inline arrays on every render.
      return normalizedItems
         .map((item, idx) => `${item?.key ?? idx}`)
         .join('|');
   }, [normalizedItems]);

   const safeKeepStart = Math.max(0, keepStart);
   const safeKeepEnd = Math.max(0, keepEnd);
   const safeHideFrom = ['start', 'middle', 'end'].includes(hideFrom) ? hideFrom : 'start';
   const maxHide = Math.max(0, normalizedItems.length - safeKeepStart - safeKeepEnd);

   useEffect(() => {
      setHiddenCount((prev) => (prev === 0 ? prev : 0));
      setLayoutTick((tick) => tick + 1);
      setIsMenuOpen(false);
   }, [normalizedItemsSignature, normalizedItems.length, separator, safeKeepStart, safeKeepEnd, safeHideFrom]);

   useEffect(() => {
      const host = containerRef.current;
      if (!host || typeof ResizeObserver === 'undefined') {
         return undefined;
      }

      const observer = new ResizeObserver(() => {
         setHiddenCount(0);
         setLayoutTick((tick) => tick + 1);
      });

      observer.observe(host);
      return () => observer.disconnect();
   }, []);

   useLayoutEffect(() => {
      const host = containerRef.current;
      if (!host) {
         return;
      }

      const breadcrumbNode = host.querySelector('.responsive-breadcrumb');
      if (!breadcrumbNode) {
         return;
      }

      const listNode = breadcrumbNode.querySelector('.responsive-breadcrumb__list') || breadcrumbNode;
      // Iteratively hide one middle item until the breadcrumb fits the current width.
      const isOverflowing = listNode.scrollWidth > breadcrumbNode.clientWidth + 1;
      if (isOverflowing && hiddenCount < maxHide) {
         setHiddenCount((prev) => Math.min(prev + 1, maxHide));
      }
   }, [hiddenCount, maxHide, layoutTick]);

   useEffect(() => {
      if (!isMenuOpen) {
         return undefined;
      }

      const onPointerDown = (event) => {
         if (!menuRef.current?.contains(event.target)) {
            setIsMenuOpen(false);
         }
      };

      const onEscape = (event) => {
         if (event.key === 'Escape') {
            setIsMenuOpen(false);
         }
      };

      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('keydown', onEscape);
      return () => {
         document.removeEventListener('mousedown', onPointerDown);
         document.removeEventListener('keydown', onEscape);
      };
   }, [isMenuOpen]);

   const {
      leadingItems,
      beforeHiddenMiddleItems,
      hiddenItems,
      afterHiddenMiddleItems,
      trailingItems,
   } = useMemo(() => {
      // `middle` is the collapsible zone between pinned start/end items.
      const start = normalizedItems.slice(0, safeKeepStart);
      const end = safeKeepEnd > 0 ? normalizedItems.slice(normalizedItems.length - safeKeepEnd) : [];
      const middle = normalizedItems.slice(safeKeepStart, normalizedItems.length - safeKeepEnd);
      const safeHiddenCount = Math.min(hiddenCount, middle.length);

      if (safeHiddenCount <= 0) {
         return {
            leadingItems: start,
            beforeHiddenMiddleItems: middle,
            hiddenItems: [],
            afterHiddenMiddleItems: [],
            trailingItems: end,
         };
      }

      if (safeHideFrom === 'end') {
         return {
            leadingItems: start,
            beforeHiddenMiddleItems: middle.slice(0, middle.length - safeHiddenCount),
            hiddenItems: middle.slice(middle.length - safeHiddenCount),
            afterHiddenMiddleItems: [],
            trailingItems: end,
         };
      }

      if (safeHideFrom === 'middle') {
         // Keep hidden block centered for a more balanced breadcrumb shape.
         const hideStart = Math.floor((middle.length - safeHiddenCount) / 2);
         const hideEnd = hideStart + safeHiddenCount;

         return {
            leadingItems: start,
            beforeHiddenMiddleItems: middle.slice(0, hideStart),
            hiddenItems: middle.slice(hideStart, hideEnd),
            afterHiddenMiddleItems: middle.slice(hideEnd),
            trailingItems: end,
         };
      }

      return {
         leadingItems: start,
         beforeHiddenMiddleItems: [],
         hiddenItems: middle.slice(0, safeHiddenCount),
         afterHiddenMiddleItems: middle.slice(safeHiddenCount),
         trailingItems: end,
      };
   }, [normalizedItems, safeKeepStart, safeKeepEnd, hiddenCount, safeHideFrom]);

   // Renders a breadcrumb node as raw JSX, anchor, button, or plain text based on item shape.
   const renderItemNode = (item, { closeMenuOnClick = false } = {}) => {
      if (!item) {
         return null;
      }

      if (React.isValidElement(item.node) || !isSimpleType(item.node)) {
         return item.node ?? null;
      }

      const handleClick = (event) => {
         if (item.onClick) {
            item.onClick(event);
         }

         if (closeMenuOnClick) {
            setIsMenuOpen(false);
         }
      };

      if (item.href) {
         return (
            <a href={item.href} onClick={handleClick} target={item.target} rel={item.rel}>
               {item.node}
            </a>
         );
      }

      if (item.onClick) {
         return (
            <button type="button" className="responsive-breadcrumb__link-btn" onClick={handleClick}>
               {item.node}
            </button>
         );
      }

      return item.node;
   };

   const visibleItems = [
      ...leadingItems,
      ...beforeHiddenMiddleItems,
      ...(hiddenItems.length > 0 ? [{ key: '__collapsed__', isCollapsed: true }] : []),
      ...afterHiddenMiddleItems,
      ...trailingItems,
   ];

   return (
      <div ref={containerRef} className="responsive-breadcrumb-wrap" style={style}>
         <nav className="responsive-breadcrumb" aria-label="Breadcrumb">
            <ol className="responsive-breadcrumb__list">
               {visibleItems.map((item, idx) => {
                  const isLast = idx === visibleItems.length - 1;
                  const key = item.key ?? `crumb-${idx}`;

                  return (
                     <React.Fragment key={key}>
                        <li className="responsive-breadcrumb__item" aria-current={isLast ? 'page' : undefined}>
                           {item.isCollapsed ? (
                              <div ref={menuRef} className="responsive-breadcrumb__menu-wrap">
                                 <button
                                    type="button"
                                    className="responsive-breadcrumb__ellipsis"
                                    onClick={() => setIsMenuOpen((open) => !open)}
                                    aria-label="More breadcrumb items"
                                    aria-expanded={isMenuOpen}
                                 >
                                    ...
                                 </button>
                                 {isMenuOpen && (
                                    <ul className="responsive-breadcrumb__menu" role="menu">
                                       {hiddenItems.map((hiddenItem, hiddenIdx) => (
                                          <li key={hiddenItem.key ?? `hidden-${hiddenIdx}`} className="responsive-breadcrumb__menu-item" role="menuitem">
                                             {renderItemNode(hiddenItem, { closeMenuOnClick: true })}
                                          </li>
                                       ))}
                                    </ul>
                                 )}
                              </div>
                           ) : (
                              <span className="responsive-breadcrumb__node">{renderItemNode(item)}</span>
                           )}
                        </li>
                        {!isLast && <li className="responsive-breadcrumb__separator" aria-hidden="true">{separator}</li>}
                     </React.Fragment>
                  );
               })}
            </ol>
         </nav>
      </div>
   );
};

ResponsiveBreadcrumb.Item = ResponsiveBreadcrumbItem;

export default ResponsiveBreadcrumb;

